import React, { useState } from 'react';
import { Modal, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { API_ENDPOINTS } from '../../../config/api.config';
import { CloudProvider } from '../../../types/cloud';
import { apiService } from '../../../services/apiService';

interface CreateOrgModalProps {
    show: boolean;
    onHide: () => void;
    onSuccess: () => void;
}

interface CreateOrgFormData {
    name: string;
    domain: string;
    adminEmail: string;
    adminFullName: string;
    adminPassword: string;
    cloudProvider: CloudProvider;
}

interface CloudResourcesState {
    status: 'idle' | 'creating' | 'success' | 'error';
    error?: string;
}

interface CloudResourcesResponse {
    bucketName: string;
    auditBucketName: string;
}

interface CreateOrgResponse {
    id: string;
    // Add other response fields as needed
}

const CreateOrgModal: React.FC<CreateOrgModalProps> = ({ show, onHide, onSuccess }) => {
    const [formData, setFormData] = useState<CreateOrgFormData>({
        name: '',
        domain: '',
        adminEmail: '',
        adminFullName: '',
        adminPassword: '',
        cloudProvider: 'aws' // Default to AWS
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [cloudResources, setCloudResources] = useState<CloudResourcesState>({
        status: 'idle'
    });

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (error) setError('');
        if (cloudResources.status === 'error') {
            setCloudResources({ status: 'idle' });
        }
    };

    const validateForm = (): boolean => {
        // ... (keep existing validation logic)
        return true;
    };

    const verifyCloudResources = async (orgId: string): Promise<boolean> => {
        try {
            const data = await apiService.get<CloudResourcesResponse>(
                API_ENDPOINTS.ORGANIZATIONS.CLOUD_RESOURCES.replace(':id', orgId)
            );

            return !!(data && data.bucketName && data.auditBucketName);
        } catch (err) {
            setCloudResources({
                status: 'error',
                error: 'Failed to verify cloud resources'
            });
            return false;
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        setLoading(true);
        setError('');
        setCloudResources({ status: 'creating' });

        try {
            const orgData = await apiService.post<CreateOrgResponse>(
                API_ENDPOINTS.ORGANIZATIONS.CREATE,
                formData
            );

            const resourcesVerified = await verifyCloudResources(orgData.id);

            if (resourcesVerified) {
                setCloudResources({ status: 'success' });
                onSuccess();
                handleClose();
            } else {
                onSuccess();
                setError('Organization created but cloud resources may need attention');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
            setCloudResources({
                status: 'error',
                error: 'Organization creation failed'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            name: '',
            domain: '',
            adminEmail: '',
            adminFullName: '',
            adminPassword: '',
            cloudProvider: 'aws'
        });
        setError('');
        setCloudResources({ status: 'idle' });
        onHide();
    };

    const renderCloudStatus = () => {
        switch (cloudResources.status) {
            case 'creating':
                return (
                    <Alert variant="info" className="mb-3">
                        <Spinner size="sm" className="me-2" />
                        Creating cloud resources...
                    </Alert>
                );
            case 'success':
                return (
                    <Alert variant="success" className="mb-3">
                        Cloud resources created successfully
                    </Alert>
                );
            case 'error':
                return (
                    <Alert variant="warning" className="mb-3">
                        {cloudResources.error}
                    </Alert>
                );
            default:
                return null;
        }
    };

    return (
        <Modal show={show} onHide={handleClose} backdrop="static" size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Create New Organization</Modal.Title>
            </Modal.Header>

            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
                    {renderCloudStatus()}

                    <div className="mb-4">
                        <h6 className="text-muted mb-3">Organization Details</h6>

                        <Form.Group className="mb-3">
                            <Form.Label>Organization Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                placeholder="Example Organization"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Domain</Form.Label>
                            <Form.Control
                                type="text"
                                name="domain"
                                value={formData.domain}
                                onChange={handleChange}
                                required
                                placeholder="example.com"
                            />
                            <Form.Text className="text-muted">
                                Enter the primary domain for this organization
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Cloud Provider</Form.Label>
                            <Form.Select
                                name="cloudProvider"
                                value={formData.cloudProvider}
                                onChange={handleChange}
                                required
                            >
                                <option value="aws">Amazon Web Services (AWS)</option>
                                <option value="gcp">Google Cloud Platform (GCP)</option>
                            </Form.Select>
                            <Form.Text className="text-muted">
                                Select the cloud provider for storage and resources
                            </Form.Text>
                        </Form.Group>
                    </div>

                    <div>
                        <h6 className="text-muted mb-3">Admin User Details</h6>
                        <Form.Group className="mb-3">
                            <Form.Label>Admin Full Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="adminFullName"
                                value={formData.adminFullName}
                                onChange={handleChange}
                                required
                                placeholder="John Doe"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Admin Email</Form.Label>
                            <Form.Control
                                type="email"
                                name="adminEmail"
                                value={formData.adminEmail}
                                onChange={handleChange}
                                required
                                placeholder="admin@example.com"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Admin Password</Form.Label>
                            <Form.Control
                                type="password"
                                name="adminPassword"
                                value={formData.adminPassword}
                                onChange={handleChange}
                                required
                                minLength={8}
                            />
                            <Form.Text className="text-muted">
                                Password must be at least 8 characters long
                            </Form.Text>
                        </Form.Group>
                    </div>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={loading || cloudResources.status === 'creating'}
                    >
                        {loading || cloudResources.status === 'creating' ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Creating Organization...
                            </>
                        ) : (
                            'Create Organization'
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default CreateOrgModal;